import {
  buildEventRow,
  createEventResultText,
  todayYmdInZone,
  type EventRowFields,
  type EventSource,
} from "./createEventBuilder.ts";
import { durableBirthdayNote, writeDurableBirthday } from "./birthdayDurableWrite.ts";
// #1694 — the SAME predicates the browser uses for voice's other three writes.
// They moved to _shared so this module could read a refusal without a second
// copy; src/lib/voiceWriteBudget.ts re-exports them, so every existing caller is
// unchanged. See that file's header for why the direction is Edge → src.
import { isDuplicateProposeError, isVoiceRateLimitError } from "../_shared/voiceWriteErrors.ts";

/**
 * #1670 — create_event. The write half: her timezone, her kids, the duplicate
 * pre-check, and a verified insert.
 *
 * Kept apart from createEventBuilder.ts so all the mapping rules can be tested
 * without a database client. Everything with a `client` in it lives here.
 */

export interface CreateEventClient {
  from: (table: string) => any;
}

/**
 * Which of the three things actually happened — added as part of #1686.
 *
 * `event === null` covers THREE different outcomes that kin-chat never had to
 * tell apart, because it hands `resultText` straight to the model and shows a
 * receipt chip whenever there is an event. Voice has to tell them apart: it
 * decides whether to fire a toast, and "already on her calendar" must not
 * produce one (nothing new appeared) while "did not save" must not either.
 *
 * ⚠️ Structural rather than matched on the prose. Deciding by regex against
 * resultText would put a live write path's behaviour behind a sentence someone
 * will reword, and it fails in the worst direction: a reworded duplicate message
 * starts toasting "Added to your calendar" over a row that was already there.
 */
export type CreateEventStatus =
  /** A row exists and this call wrote it. */
  | "written"
  /** A matching row was already there. Nothing was written, and that is correct. */
  | "already_there"
  /**
   * #1694 — THIS EXACT PROPOSAL already wrote a row. A success, and a different
   * fact from `already_there`.
   *
   * ⚠️ The two must not be merged. `already_there` is a TITLE+DATE match: some
   * other row that happens to look the same, which is a genuine product judgement
   * ("you already have Dentist that day"). `duplicate` is an IDENTITY match on
   * the propose id: this very confirmation has already been honored, so the right
   * thing to say is nothing at all. Kin apologising for a double-booking she did
   * not make, or announcing an event twice off one yes, are both wrong.
   */
  | "duplicate"
  /**
   * #1694 — the per-window voice write cap refused it. Nothing was written.
   *
   * ⚠️ Structurally distinct from `not_saved` even though both mean "no row",
   * because the two need opposite things said out loud. "It didn't save, try
   * again" invites the retry the cap exists to break; the cap needs "I've hit my
   * limit for a few minutes, I'll hold off." Voice maps this to
   * VOICE_WRITE_RATE_LIMITED_RESULT, which says exactly that and says don't retry.
   */
  | "rate_limited"
  /** Nothing exists. Rejected input, a past date, or a failed insert. */
  | "not_saved";

export interface CreateEventOutcome {
  resultText: string;
  event: Record<string, unknown> | null;
  status: CreateEventStatus;
}

/**
 * `events` has NO timezone column — a naked `date` plus a text clock string —
 * and the edge runtime is UTC. So "add this for tomorrow" sent at 9pm Eastern
 * resolves against the wrong day unless the date is computed on HER wall
 * clock. Same defect class as #1651.
 *
 * Reads the same `profiles.timezone` column `buildAmbientCalendarContext`
 * does, and falls back the same way: a slightly wrong day still writes the
 * event; a throw loses it.
 */
export async function userTimeZone(client: CreateEventClient, userId: string): Promise<string> {
  let fromProfile: string | null = null;
  try {
    const { data, error } = await client
      .from("profiles")
      .select("timezone")
      .eq("id", userId)
      .maybeSingle();
    // A failed read is NOT the same as a null timezone (#701/#982). Both end
    // in the same place, but only one is a bug.
    if (error) console.error("[create_event] profile timezone query error:", error);
    fromProfile = data?.timezone || null;
  } catch (e) {
    console.error("[create_event] profile timezone read threw:", e);
  }
  if (fromProfile) return fromProfile;

  // Second chance, exactly kin-sms's order (index.ts:397-434). profiles.timezone
  // is the source of truth 20260725120000 established, filled from the device
  // on app open, and that migration's header records notifications_timezone as
  // "mostly NULL — a notifications artifact". But mostly-NULL is not always
  // NULL: a mom who set a morning-note timezone and never opened the app on a
  // device that filled profiles has only this one, and falling straight to
  // Eastern can reject her valid local date as being in the past.
  // (Copilot on #1681.)
  try {
    const { data, error } = await client
      .from("user_preferences")
      .select("notifications_timezone")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) console.error("[create_event] preferences timezone query error:", error);
    if (data?.notifications_timezone) return data.notifications_timezone;
  } catch (e) {
    console.error("[create_event] preferences timezone read threw:", e);
  }
  // The same fallback user_cap_day() uses, so the two never disagree about
  // which day it is for her.
  return "America/New_York";
}

/**
 * Her kids' names, for matching ONLY — never for guessing. A read failure
 * returns an empty list, which makes `child` null: the safe direction, since
 * a wrong name here becomes a hallucinated child in her 7am briefing.
 */
export async function knownKidNames(client: CreateEventClient, userId: string): Promise<string[]> {
  const names = new Set<string>();

  // `user_preferences.known_kid_names` is what the dispatcher treats as the
  // authoritative list (dispatch-notifications:1839-1888), and a kid can be
  // configured there with no family_context fact at all — reading only
  // family_context loses a real association and stores `child` as null.
  // (Copilot on #1681.) Both sources are merged rather than ranked: this list
  // is used ONLY to match a name the model supplied, never to guess one, so a
  // wider list cannot invent a child.
  try {
    const { data, error } = await client
      .from("user_preferences")
      .select("known_kid_names")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) console.error("[create_event] known_kid_names query error:", error);
    if (Array.isArray(data?.known_kid_names)) {
      for (const raw of data.known_kid_names) {
        const name = typeof raw === "string" ? raw.trim() : "";
        if (name) names.add(name);
      }
    }
  } catch (e) {
    console.error("[create_event] known_kid_names read threw:", e);
  }

  try {
    const { data, error } = await client
      .from("family_context")
      .select("subject")
      .eq("user_id", userId)
      .eq("category", "kid")
      .eq("archived", false);
    // A read failure contributes nothing rather than emptying the set: the
    // other source may still have her kids, and `child` null is the safe
    // direction either way.
    if (error) {
      console.error("[create_event] kid names query error:", error);
      return [...names];
    }
    for (const row of data || []) {
      const subject = typeof row?.subject === "string" ? row.subject.trim() : "";
      if (subject) names.add(subject);
    }
  } catch (e) {
    console.error("[create_event] kid names read threw:", e);
  }
  return [...names];
}

/**
 * Nothing at the database level stops a duplicate — no unique index, no
 * trigger. The existing `find_event_duplicates` RPC queries `imported_events`
 * and is photo-import only, so it cannot be reused. Same pre-check every
 * direct writer does.
 *
 * A read FAILURE returns false (not a duplicate): refusing to write on an
 * unreadable check would lose the event she asked for, and a duplicate she can
 * see and delete is the cheaper error.
 */
export async function findDuplicate(
  client: CreateEventClient,
  userId: string,
  fields: EventRowFields,
): Promise<boolean> {
  try {
    const { data, error } = await client
      .from("events")
      .select("id")
      .eq("user_id", userId)
      .eq("event_date", fields.event_date)
      .eq("title", fields.title)
      .eq("status", "active")
      .limit(1);
    if (error) {
      console.error("[create_event] duplicate pre-check failed:", error);
      return false;
    }
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.error("[create_event] duplicate pre-check threw:", e);
    return false;
  }
}

/**
 * Postgres `undefined_column` plus PostgREST's pre-refresh missing-column shape.
 * #1694 — the only errors that mean "the migration has not run yet" rather than
 * "this write is wrong".
 *
 * ⚠️ 42703 is stable and matched by code alone. PGRST204 is only accepted when
 * its message/details/hint names BOTH the target relation (`events`) and the
 * specific column (`propose_id`) so unrelated schema-cache errors do not trigger
 * the deploy-order retry.
 */
export const UNDEFINED_COLUMN = "42703";
export const POSTGREST_UNDEFINED_COLUMN = "PGRST204";

export function isUndefinedColumnError(err: unknown): boolean {
  const e = err as { code?: string; message?: string; details?: string; hint?: string } | null;
  const code = String(e?.code ?? "");
  if (code === UNDEFINED_COLUMN) return true;
  if (code !== POSTGREST_UNDEFINED_COLUMN) return false;
  const text = `${e?.message ?? ""}\n${e?.details ?? ""}\n${e?.hint ?? ""}`.toLowerCase();
  return text.includes("propose_id") && text.includes("events");
}

/**
 * #1694 — has THIS proposal already written a row?
 *
 * Identity, not similarity: `findDuplicate` above asks whether something that
 * looks the same is on her calendar, which is a different and genuinely arguable
 * question. This asks whether this very confirmation has already been honored.
 *
 * ⚠️ A read FAILURE returns null — "not a duplicate" — which lets the insert
 * proceed. That is the right direction: the unique index still catches a real
 * collision, so the worst case is a wasted insert attempt rather than a lost
 * event. Refusing to write on an unreadable check would drop the thing she just
 * asked for out loud.
 *
 * ⚠️ Deliberately NOT filtered on status or dismissal. A retry must not
 * resurrect an event she has since deleted or swiped away — the row existing at
 * all is the whole question, exactly as save_voice_todo does not filter on
 * `checked`.
 */
export async function findByProposeId(
  client: CreateEventClient,
  userId: string,
  proposeId: string,
): Promise<boolean> {
  try {
    const { data, error } = await client
      .from("events")
      .select("id")
      .eq("user_id", userId)
      .eq("propose_id", proposeId)
      .limit(1);
    if (error) {
      // 42703 is the expected state before the migration runs, not a fault — and
      // logging it as an error would put a red line in the dispatch logs every
      // time she says yes to an event, which is how a real error gets missed.
      if (isUndefinedColumnError(error)) {
        console.warn("[create_event] events.propose_id is not live yet (#1694 SQL pending) — skipping the pre-check");
      } else {
        console.error("[create_event] propose-id pre-check failed:", error);
      }
      return false;
    }
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.error("[create_event] propose-id pre-check threw:", e);
    return false;
  }
}

export async function executeCreateEventTool(
  input: any,
  client: CreateEventClient,
  userId: string,
  // #1686 — voice calls this too, with the BROWSER supabase client (see
  // src/lib/voiceCreateEvent.ts). Defaulted so kin-chat's call site and the
  // #1670 tests are untouched.
  //
  // ⚠️ Nothing else in this function changes for voice, and that is the point
  // of routing voice through it rather than writing a second insert in the
  // browser. Her timezone, the kid-name match, the past-date guard, the
  // duplicate pre-check, the house-rule-16 verified read and the durable
  // birthday write are all behaviour voice would otherwise have had to
  // reimplement — which is the three-hand-synced-copies defect shape this
  // codebase produces more than any other, and what #1685 had just finished
  // undoing on the persona side.
  source: EventSource = "kin_chat",
  // #1694 — minted by the voice capture gate when Kin PROPOSES the event, so the
  // id is fixed before she says yes. Null for chat, which has no propose step:
  // the column is written as null and the unique index below is partial, so
  // chat's rows are not indexed at all.
  proposeId: string | null = null,
  // #1721 — whose birthday goes where depends on her tier: Pro gets a real
  // Birthday Reminder contact, free saves no non-kid birthday at all. Passed
  // IN rather than re-read, because kin-chat has already computed it
  // (`memoryEnabled = isPro || isAdmin`) in the scope that calls this, and a
  // second read could disagree with the gate that let the tool be offered.
  //
  // Defaulted to null = "caller has not been updated", which keeps the
  // pre-#1721 family_context fallback rather than silently dropping a
  // birthday. The voice path (src/lib/voiceCreateEvent.ts) is the one that
  // still relies on this; #1686's follow-up should pass a real tier.
  tier: { isPro: boolean } | null = null,
): Promise<CreateEventOutcome> {
  const tz = await userTimeZone(client, userId);
  const kids = await knownKidNames(client, userId);

  const built = buildEventRow(
    {
      title: input?.title,
      eventDate: input?.event_date,
      timeMode: input?.time_mode,
      startTime: input?.start_time,
      location: input?.location,
      child: input?.child,
    },
    kids,
    source,
    proposeId,
  );
  // Read structurally, not by narrowing: this module is checked by BOTH
  // `deno check` (strict) and the repo's tsconfig (strictNullChecks off).
  if (!built.ok) {
    return { resultText: (built as { error: string }).error, event: null, status: "not_saved" };
  }
  const fields: EventRowFields = built.fields;
  const timeMode = built.timeMode;

  // #1694 — IDEMPOTENCY FIRST, before any other judgement about this call.
  //
  // If this exact proposal already wrote a row, nothing else is relevant: not the
  // date, not a title match, not the cap. The same shape as save_voice_todo, and
  // for its stated reason — "a repeat of a call that already succeeded must return
  // the same answer, not a second to-do."
  //
  // ⚠️ Ordered ABOVE the past-date guard deliberately. A retry of a call that
  // landed must not come back complaining about the date of an event that is
  // already on her calendar.
  //
  // ⚠️ This lookup is the FRIENDLY path, not the guarantee. Two simultaneous
  // calls can both read "not found" and both insert; the partial unique index on
  // (user_id, propose_id) is what makes the loser fail, and the insert below
  // recognises that failure as a success. A lookup alone would be a race.
  if (proposeId) {
    const existing = await findByProposeId(client, userId, proposeId);
    if (existing) {
      return {
        resultText:
          `That one is already on her calendar — you saved it a moment ago. ` +
          `Do not save it again and do not mention a duplicate; just carry on.`,
        event: null,
        status: "duplicate",
      };
    }
  }

  // A date in her past is almost always a model arithmetic slip against a UTC
  // "today", not something she asked for. Say so rather than burying an event
  // where she will never look.
  const today = todayYmdInZone(new Date(), tz);
  if (fields.event_date < today) {
    return {
      resultText:
        `Did not add it: ${fields.event_date} is in the past — today is ${today} where she lives. ` +
        `Nothing was saved. Work out the date again from today, or ask her which day she meant.`,
      event: null,
      status: "not_saved",
    };
  }

  if (await findDuplicate(client, userId, fields)) {
    return {
      resultText:
        `"${fields.title}" is already on her calendar for ${fields.event_date}, so nothing was added. ` +
        `Tell her it was already there — do not say you added it again.`,
      event: null,
      status: "already_there",
    };
  }

  try {
    // ⚠️ House rule 16. supabase-js RESOLVES on an RLS refusal, and a write
    // matching zero rows returns 200 with `error === null` — so neither the
    // absence of an error nor a 2xx is evidence a row exists. Ask for the row
    // back and assert it came.
    let { data, error } = await client
      .from("events")
      .insert({ ...fields, user_id: userId })
      .select("id")
      .single();

    // #1694 — ⚠️ THE PRE-MIGRATION WINDOW. `events.propose_id` does not exist
    // until Kasandra runs 20260924090000 by hand, and this repo cannot prove a
    // migration ran (`schema_migrations` is only written by `db push`). So between
    // this code deploying and that SQL running, an insert naming the column fails
    // with 42703 — and the honest answer to "she just said yes to an event" is
    // never "nothing happened because a column is missing".
    //
    // So: retry once WITHOUT the propose id. She gets her event, unindexed, which
    // is exactly the #1686 behaviour this issue is hardening — and the retry
    // disappears on its own the moment the column is live. Same hedge kin-chat's
    // create_todo already uses for `todo_lists.is_default`.
    //
    // Chat cannot reach this at all: its rows omit the key entirely (see
    // EventRowFields.propose_id), so there is nothing for PostgREST to object to.
    if (error && isUndefinedColumnError(error) && (fields as { propose_id?: string }).propose_id) {
      console.warn("[create_event] events.propose_id is not live yet (#1694 SQL pending) — inserting without it");
      const { propose_id: _pendingColumn, ...withoutProposeId } = fields as unknown as Record<string, unknown>;
      ({ data, error } = await client
        .from("events")
        .insert({ ...withoutProposeId, user_id: userId })
        .select("id")
        .single());
    }

    if (error || !data?.id) {
      // #1694 — three different failures used to collapse into one sentence, and
      // two of them are not failures at all.
      //
      // ⚠️ A propose-id collision means THE ROW IS THERE. It is the race the
      // unique index exists to lose: another call carrying the same proposal got
      // in first. Reporting it as "it did not save" would make the model retry a
      // write that succeeded, and tell her the opposite of the truth — the
      // precise defect `isDuplicateProposeError`'s header describes.
      if (isDuplicateProposeError(error)) {
        return {
          resultText:
            `That one is already on her calendar — you saved it a moment ago. ` +
            `Do not save it again and do not mention a duplicate; just carry on.`,
          event: null,
          status: "duplicate",
        };
      }
      // ⚠️ The cap is not a malfunction, and "try again" is the one thing that
      // must not be said about it: a refusal the model reads as transient is a
      // refusal it retries against, which is the loop the cap exists to break.
      if (isVoiceRateLimitError(error)) {
        console.warn("[create_event] voice write cap refused the insert");
        return {
          resultText:
            "She has saved a lot in this one conversation already and has hit the limit for now. " +
            "Nothing was saved. Tell her you'll hold off for a few minutes, and carry on talking normally. Do NOT retry.",
          event: null,
          status: "rate_limited",
        };
      }
      console.error("[create_event] insert failed or returned no row:", error);
      return {
        resultText:
          "Could not add that to her calendar just now. Tell her plainly that it did NOT save and to try again — " +
          "do not say it's on her calendar.",
        event: null,
        status: "not_saved",
      };
    }
    // #1670's amendment — the recurring fact comes AFTER the row is confirmed,
    // so a failed event insert never leaves a birthdate behind for an event
    // that does not exist. Its own failure is reported rather than swallowed:
    // "Kin will remember it" over an unwritten fact is the false confirmation
    // house rule 16 exists to prevent.
    // ⚠️ `source` threads through here too. Stamping only the `events` row left
    // a voice-created birthday FACT labelled "chat", which slipped past
    // enforce_voice_fact_budget — scoped to source = 'voice'. (Copilot, #1695.)
    const birthday = await writeDurableBirthday(
      client,
      userId,
      fields.title,
      fields.event_date,
      kids,
      source,
      tier,
      today,
      tz,
    );
    return {
      resultText: createEventResultText(fields, timeMode) + durableBirthdayNote(birthday),
      status: "written",
      event: {
        type: "event_receipt",
        id: data.id,
        title: fields.title,
        eventDate: fields.event_date,
        isAllDay: fields.is_all_day,
        startTime: fields.start_time,
      },
    };
  } catch (e) {
    console.error("[create_event] insert threw:", e);
    return {
      resultText:
        "Could not add that to her calendar just now. Tell her plainly that it did NOT save and to try again — " +
        "do not say it's on her calendar.",
      event: null,
      status: "not_saved",
    };
  }
}
